/**
 * HTTP API contract shared by apps/api and apps/web.
 * All routes are under /api. All bodies are JSON. Auth: `Authorization: Bearer <Cognito ID token>`
 * (in local dev: `x-dev-user: <userId>`). Errors: `{ error: { code, message } }` with a 4xx/5xx status.
 */
import { z } from 'zod';
import { PhoneNumber } from './phone';

/** Largest original accepted (upload contract, processing and renditions). */
export const MAX_ORIGINAL_BYTES = 200 * 1024 * 1024;
/** Files in one Flock post. */
export const MAX_FLOCK_FILES = 20;
import { Activity, ActivityAction, Album, AlbumName, Collaborator, GeoLocation, Orient, Photo, Profile, Region, Source, SourceScope, Tag, TagCategory, TagName, User, UserType, Visibility } from './model';

/* ---------- me ---------- */
export const MeResponse = z.object({
  user: User.nullable(), // null = signed in but never requested access
  /** The caller's shared JBCH USA profile (may exist from another app before they request Photos access). */
  profile: Profile.nullable().default(null),
  capabilities: z.object({
    media: z.boolean(),
    release: z.boolean(),
    restricted: z.boolean(),
    admin: z.boolean(),
    studio: z.boolean(),
    share: z.boolean(),
  }),
  /** Albums the caller was given access to (invite link or admin grant): they may upload and tag there. */
  collaboratorAlbumIds: z.array(z.string()).default([]),
});
export type MeResponse = z.infer<typeof MeResponse>;

/**
 * POST /api/me/request-access. Profile fields fill in (or update) the shared profile; someone who already has one only
 * needs to send the reason plus whatever the profile is missing (name, church and region are required overall).
 */
export const AccessRequestBody = z.object({
  name: z.string().trim().min(1).max(80).optional(),
  /** A church tag name, e.g. 'new-york'. */
  church: TagName.optional(),
  region: Region.optional(),
  /** Fellowship group tag (at most one). */
  fellowship: TagName.nullable().optional(),
  /** Service group tags (any number). */
  services: z.array(TagName).max(20).optional(),
  /** Phone, E.164 (the form normalizes it for the chosen country). */
  phone: PhoneNumber.nullable().optional(),
  reason: z.string().max(280).default(''),
});
export type AccessRequestBody = z.infer<typeof AccessRequestBody>;

/** PATCH /api/me/profile: an active user edits their own profile (region, church, groups, phone; null clears the phone). */
export const UpdateProfileBody = z
  .object({
    region: Region,
    church: z.union([TagName, z.literal('')]),
    fellowship: TagName.nullable(),
    services: z.array(TagName).max(20),
    phone: PhoneNumber.nullable(),
  })
  .partial()
  .refine((b) => Object.keys(b).length > 0, 'Nothing to update');
export type UpdateProfileBody = z.infer<typeof UpdateProfileBody>;

/** GET /api/me/options: church, fellowship and service tags for the request-access form (any signed-in caller). */
const TagOption = z.object({ name: TagName, label: z.string() });
export const ProfileOptionsResponse = z.object({
  /** Sorted by label; `region` lets the forms group them (East / Central / West). */
  churches: z.array(TagOption.extend({ region: Region.optional() })),
  fellowships: z.array(TagOption),
  services: z.array(TagOption),
});
export type ProfileOptionsResponse = z.infer<typeof ProfileOptionsResponse>;

/* ---------- photos ---------- */
/** What the grid needs per photo (URLs are short-lived signed URLs). */
export const PhotoCard = Photo.pick({
  id: true,
  source: true,
  origin: true,
  visibility: true,
  tags: true,
  takenAt: true,
  uploaderName: true,
  uploaderId: true,
  uploadedAt: true,
  homeAlbumId: true,
  caption: true,
  width: true,
  height: true,
  orient: true,
  picked: true,
  thumbhash: true,
  bytes: true,
  fileName: true,
}).extend({
  thumbUrl: z.string(),
  previewUrl: z.string(),
});
export type PhotoCard = z.infer<typeof PhotoCard>;

export const PhotoDetail = PhotoCard.extend({
  camera: z.string().optional(),
  contentType: z.string(),
  originalKey: z.string(),
  /** Home album first, then the albums it was added to. */
  albums: z.array(z.object({ id: z.string(), name: z.string(), home: z.boolean() })),
});
export type PhotoDetail = z.infer<typeof PhotoDetail>;

export const SearchQuery = z.object({
  source: SourceScope.default('glean'),
  tags: z.array(TagName).default([]),
  picksOnly: z.coerce.boolean().default(false),
  orient: Orient.optional(),
  cursor: z.string().optional(),
  limit: z.coerce.number().int().min(1).max(200).default(120),
});
export type SearchQuery = z.infer<typeof SearchQuery>;

export const SearchResponse = z.object({
  items: z.array(PhotoCard),
  cursor: z.string().nullable(),
  /** Totals for the whole result set (not just this page). */
  total: z.number().int(),
  totalBytes: z.number().int(),
  firstTakenAt: z.string().nullable(),
  lastTakenAt: z.string().nullable(),
  /** Count per shape across the result set ignoring `orient`, for the shape switch. */
  orientCounts: z.object({ h: z.number(), v: z.number(), sq: z.number() }),
  /** Co-occurring tags in the result set (for "Narrow down"), most frequent first. */
  related: z.array(z.object({ tag: TagName, count: z.number() })),
});
export type SearchResponse = z.infer<typeof SearchResponse>;

/** PATCH /api/flock/:id: a Flock poster edits the tags on their own post photo. */
export const FlockTagsBody = z
  .object({ addTags: z.array(TagName).default([]), removeTags: z.array(TagName).default([]) })
  .refine((b) => b.addTags.length + b.removeTags.length > 0, 'Nothing to update');
export type FlockTagsBody = z.infer<typeof FlockTagsBody>;

export const DownloadSize = z.enum(['original', 'large', 'social']);
export type DownloadSize = z.infer<typeof DownloadSize>;

export const DownloadUrlsBody = z.object({ ids: z.array(z.string()).min(1).max(2000), size: DownloadSize });
export const DownloadUrlsResponse = z.object({
  files: z.array(z.object({ id: z.string(), name: z.string(), url: z.string(), bytes: z.number() })),
});
export type DownloadUrlsResponse = z.infer<typeof DownloadUrlsResponse>;

/* ---------- browse (sidebar + category pages) ---------- */
export const TagCount = z.object({ tag: TagName, label: z.string(), category: TagCategory, count: z.number() });
export type TagCount = z.infer<typeof TagCount>;

export const NavResponse = z.object({
  total: z.number(),
  picks: z.number(),
  totalBytes: z.number(),
  years: z.array(TagCount),
  regions: z.array(TagCount),
  /** Event + year combinations, e.g. "Summer Retreat 2026". */
  events: z.array(z.object({ tags: z.array(TagName), label: z.string(), count: z.number(), lastTakenAt: z.string() })),
  churches: z.array(TagCount),
  /** Fellowship groups first, then service groups (one Groups section in the sidebar). */
  groups: z.object({ fellowship: z.array(TagCount), service: z.array(TagCount) }),
  popular: z.array(TagCount),
});
export type NavResponse = z.infer<typeof NavResponse>;

export const BrowseKind = z.enum(['years', 'regions', 'events', 'churches', 'groups']);
export type BrowseKind = z.infer<typeof BrowseKind>;

/** Category overview pages (Years / Regions / Events / Churches / Groups): cards with 3 cover photos. */
export const BrowseCard = z.object({
  tags: z.array(TagName),
  label: z.string(),
  count: z.number(),
  picks: z.number(),
  firstTakenAt: z.string(),
  lastTakenAt: z.string(),
  covers: z.array(z.object({ id: z.string(), thumbUrl: z.string() })),
  /** Tag category of a single-tag card (the Groups page splits fellowship and service cards). */
  category: TagCategory.optional(),
});
export type BrowseCard = z.infer<typeof BrowseCard>;
export const BrowseResponse = z.object({ cards: z.array(BrowseCard), total: z.number() });

export const VisibilityCounts = z.object({ released: z.number(), unreleased: z.number(), restricted: z.number() });
export type VisibilityCounts = z.infer<typeof VisibilityCounts>;

export const TagsResponse = z.object({ tags: z.array(Tag), aliases: z.record(z.string()) });

/* ---------- uploads ---------- */
/** POST /api/uploads (Glean): the album is required, either an existing one or a new one created on the spot. */
export const CreateUploadBody = z.object({
  albumId: z.string().min(1).optional(),
  /** Creates this album (the "+ New album" choice) and uploads into it. */
  newAlbumName: AlbumName.optional(),
  tags: z.array(TagName).default([]),
  /**
   * Year tags the browser suggested from capture dates ("auto") that the user kept. They are NOT applied
   * to every photo: each photo gets only its own capture year, and only if that year is in this list
   * (a removed auto year means the user didn't want it). Manually typed year tags go in `tags`.
   */
  autoYearTags: z.array(TagName).default([]),
  /** Uploads are never released directly; restricted needs restricted access. */
  visibility: z.enum(['unreleased', 'restricted']).default('unreleased'),
  files: z
    .array(
      z.object({
        clientId: z.string(), // browser-side id for matching responses
        name: z.string().max(255),
        bytes: z.number().int().positive().max(MAX_ORIGINAL_BYTES, `Files can be at most ${MAX_ORIGINAL_BYTES / 1024 ** 2} MB`),
        contentType: z.string(),
        sha256: z.string().regex(/^[a-f0-9]{64}$/),
        takenAt: z.string().optional(), // from EXIF in the browser; the server re-reads it
      }),
    )
    .min(1)
    .max(5000),
});
export type CreateUploadBody = z.infer<typeof CreateUploadBody>;

export const CreateUploadResponse = z.object({
  uploadId: z.string(),
  /** Flock posts have no album. */
  albumId: z.string().nullable(),
  albumName: z.string().nullable(),
  duplicates: z.array(z.string()), // clientIds already in Glean (skipped)
  uploads: z.array(
    z.object({
      clientId: z.string(),
      photoId: z.string(),
      /** Single PUT for files up to 100 MB; the browser sends the raw bytes with the given headers. */
      url: z.string(),
      headers: z.record(z.string()),
    }),
  ),
});
export type CreateUploadResponse = z.infer<typeof CreateUploadResponse>;

/** Browser tells the API a file finished uploading (local dev processes inline; AWS uses the S3 event). */
export const CompleteUploadBody = z.object({ photoIds: z.array(z.string()).min(1).max(5000) });

/* ---------- share links ---------- */
export const CreateShareBody = z.object({
  ids: z.array(z.string()).min(1).max(2000),
  expiresInDays: z.union([z.literal(7), z.literal(30), z.literal(90)]),
  allowDownload: z.enum(['none', 'large', 'original']),
});
export const ShareResponse = z.object({ id: z.string(), url: z.string(), expiresAt: z.string() });
export type ShareResponse = z.infer<typeof ShareResponse>;
/** Public GET /api/shares/:id (no auth). */
export const SharedPhoto = z.object({
  id: z.string(),
  width: z.number(),
  height: z.number(),
  orient: Orient,
  takenAt: z.string(),
  fileName: z.string(),
  thumbhash: z.string().optional(),
  thumbUrl: z.string(),
  previewUrl: z.string(),
  downloadUrl: z.string().nullable(),
});
export type SharedPhoto = z.infer<typeof SharedPhoto>;
export const ShareView = z.object({
  id: z.string(),
  expiresAt: z.string(),
  allowDownload: z.enum(['none', 'large', 'original']),
  photos: z.array(SharedPhoto),
});
export type ShareView = z.infer<typeof ShareView>;

/* ---------- removal requests ---------- */
/** Any viewer can ask for a photo to be taken down; the photo is hidden (trashed) right away until the media team decides. */
export const RemovalReason = z.enum(['me-or-my-child', 'inappropriate', 'duplicate-or-poor', 'other']);
export type RemovalReason = z.infer<typeof RemovalReason>;
/** `note` is optional, except for "Other": then it says why (required). */
export const RemovalRequestBody = z
  .object({ reason: RemovalReason, note: z.string().trim().max(280).default('') })
  .refine((b) => b.reason !== 'other' || b.note.length > 0, { message: 'Please tell us why you want this photo removed.' });
export type RemovalRequestBody = z.infer<typeof RemovalRequestBody>;
export const RemovalRequest = z.object({
  id: z.string(),
  photoId: z.string(),
  requesterId: z.string(),
  requesterName: z.string(),
  reason: RemovalReason,
  note: z.string(),
  createdAt: z.string(),
  status: z.enum(['open', 'restored', 'removed']),
  thumbUrl: z.string().optional(),
  /** The (hidden) photo, so reviewers can open it at full size. Only included for reviewers. */
  photo: PhotoCard.extend({ camera: z.string().optional() }).optional(),
});
export type RemovalRequest = z.infer<typeof RemovalRequest>;
export const ResolveRemovalBody = z.object({ action: z.enum(['restore', 'remove']) });
export const RemovalRequestsResponse = z.object({ items: z.array(RemovalRequest) });
export type RemovalRequestsResponse = z.infer<typeof RemovalRequestsResponse>;

/* ---------- admin ---------- */
/** A user as Admin → Users sees them, plus the albums they were given access to (invite link or admin grant). */
export const AdminUser = User.extend({ albumAccess: z.array(z.object({ id: z.string(), name: z.string() })) });
export type AdminUser = z.infer<typeof AdminUser>;

/** Profile + permission fields an admin can set. */
const UserSettings = z.object({
  type: UserType,
  canRelease: z.boolean(),
  canSeeRestricted: z.boolean(),
  isAdmin: z.boolean(),
  region: Region,
  church: z.union([TagName, z.literal('')]),
  fellowship: TagName.nullable(),
  services: z.array(TagName).max(20),
});

/**
 * POST /api/admin/users/:id/approve: permissions only. Type defaults to user and every switch to off; Can release is
 * forced off for type user (see `coupleSwitches`). The profile (region, church, groups) is what the person filled in when requesting access;
 * profile fields are rejected here (admins edit profiles later with PATCH).
 */
export const ApproveUserBody = UserSettings.pick({ type: true, canRelease: true, canSeeRestricted: true, isAdmin: true })
  .partial()
  .strict();
export type ApproveUserBody = z.infer<typeof ApproveUserBody>;
/** PATCH /api/admin/users/:id: any subset of the settings. */
export const UpdateUserBody = UserSettings.partial().refine((b) => Object.keys(b).length > 0, 'Nothing to update');
export type UpdateUserBody = z.infer<typeof UpdateUserBody>;
/** GET /api/admin/regions: one entry per region tag plus `region: null` for photos without a region tag. */
export const AdminRegionStats = z.object({
  region: Region.nullable(),
  tag: TagName.nullable(),
  label: z.string(),
  photoCount: z.number(),
  bytes: z.number(),
  unreleased: z.number(),
  restricted: z.number(),
  /** Albums that are home to at least one of these photos. */
  albums: z.number(),
  members: z.number(),
});
export type AdminRegionStats = z.infer<typeof AdminRegionStats>;
export const AdminRegionsResponse = z.object({ regions: z.array(AdminRegionStats) });
export type AdminRegionsResponse = z.infer<typeof AdminRegionsResponse>;
/** POST /api/uploads/complete response */
export const CompleteUploadResponse = z.object({
  results: z.array(z.object({ id: z.string(), status: z.enum(['ready', 'duplicate', 'failed', 'processing']), reason: z.string().optional() })),
});
export type CompleteUploadResponse = z.infer<typeof CompleteUploadResponse>;

/** User-facing names: the internal key `glean` is shown as Glean. */
export const SourceLabel: Record<z.infer<typeof Source>, string> = { glean: 'Glean', flock: 'Flock' };

export const VisibilityLabel: Record<Visibility, string> = {
  unreleased: 'Unreleased',
  released: 'Released',
  restricted: 'Restricted',
};

/* ---------- studio: albums ---------- */
export const CreateAlbumBody = z.object({
  name: AlbumName,
  description: z.string().max(500).default(''),
  /** Existing tags (any category). */
  defaultTags: z.array(TagName).max(30).default([]),
});
export type CreateAlbumBody = z.infer<typeof CreateAlbumBody>;
export const UpdateAlbumBody = z
  .object({ name: AlbumName, description: z.string().max(500), defaultTags: z.array(TagName).max(30) })
  .partial()
  .refine((b) => Object.keys(b).length > 0, 'Nothing to update');
export type UpdateAlbumBody = z.infer<typeof UpdateAlbumBody>;

/** Someone with access to an album, as Studio shows them (never the invite token). */
export const CollaboratorView = z.object({
  userId: z.string(),
  name: z.string(),
  joinedAt: z.string(),
  addedByName: z.string().nullable(),
  how: z.enum(['invite', 'direct']),
});
export type CollaboratorView = z.infer<typeof CollaboratorView>;

export const ActivityBrief = z.object({ summary: z.string(), actorName: z.string(), at: z.string() });
/** An album as the caller sees it: counts and cover come from the photos they can see in Studio. */
export const AlbumCard = Album.extend({
  counts: VisibilityCounts,
  total: z.number(),
  coverUrl: z.string().nullable(),
  lastActivity: ActivityBrief.nullable(),
  collaborators: z.array(Collaborator.pick({ userId: true, name: true })),
  /** Created by the caller, joined by them, or they uploaded into it. */
  mine: z.boolean(),
});
export type AlbumCard = z.infer<typeof AlbumCard>;
export const AlbumsResponse = z.object({ items: z.array(AlbumCard) });
export type AlbumsResponse = z.infer<typeof AlbumsResponse>;
export const AlbumDetail = AlbumCard.extend({
  /** Photos that live in this album (the rest are references to photos of other albums). */
  homeCount: z.number(),
  collaborators: z.array(CollaboratorView),
  can: z.object({ upload: z.boolean(), tag: z.boolean(), release: z.boolean(), manage: z.boolean(), organize: z.boolean() }),
});
export type AlbumDetail = z.infer<typeof AlbumDetail>;

/* ---------- studio: photos ---------- */
export const StudioSort = z.enum(['taken-desc', 'taken-asc', 'uploaded', 'name']);
export type StudioSort = z.infer<typeof StudioSort>;
/** GET /api/studio/photos: one album (`albumId`) or every album the caller can open. */
export const StudioPhotosQuery = z.object({
  albumId: z.string().optional(),
  visibility: Visibility.optional(),
  /** Photos with none of the event, region, church or group tags (a year alone counts as untagged). */
  untagged: z.coerce.boolean().default(false),
  tags: z.array(TagName).default([]),
  uploader: z.string().optional(),
  orient: Orient.optional(),
  sort: StudioSort.default('taken-desc'),
  cursor: z.string().optional(),
  limit: z.coerce.number().int().min(1).max(500).default(120),
});
export type StudioPhotosQuery = z.infer<typeof StudioPhotosQuery>;
export const StudioPhotosResponse = SearchResponse.extend({
  /** Per visibility, ignoring the visibility filter. */
  visibilityCounts: VisibilityCounts,
  /** How many photos (ignoring the untagged filter) have no category tags yet. */
  untagged: z.number(),
  uploaders: z.array(z.object({ id: z.string(), name: z.string(), count: z.number() })),
});
export type StudioPhotosResponse = z.infer<typeof StudioPhotosResponse>;
/** Studio adds the GPS location (media only) and, for "separate copies", the photo it was copied from. */
export const StudioPhotoDetail = PhotoDetail.extend({ location: GeoLocation.optional(), copiedFrom: z.string().optional() });
export type StudioPhotoDetail = z.infer<typeof StudioPhotoDetail>;

/** PATCH /api/studio/photos: bulk edits. `albumId` is the album the change was made from (for the activity log). */
export const UpdatePhotosBody = z.object({
  ids: z.array(z.string()).min(1).max(500),
  albumId: z.string().optional(),
  addTags: z.array(TagName).default([]),
  removeTags: z.array(TagName).default([]),
  picked: z.boolean().optional(),
  visibility: Visibility.optional(),
  trash: z.literal(true).optional(),
});
export type UpdatePhotosBody = z.infer<typeof UpdatePhotosBody>;
export const UpdatePhotosResponse = z.object({ items: z.array(PhotoCard), activityIds: z.array(z.string()) });
export type UpdatePhotosResponse = z.infer<typeof UpdatePhotosResponse>;

/**
 * `applyTagChanges` ("Update tags"): per photo, remove the old home album's default tags it has (unless they are also
 * defaults of the destination) and add the destination's default tags. Hand-added tags are never touched.
 */
export const MovePhotosBody = z.object({
  ids: z.array(z.string()).min(1).max(500),
  toAlbumId: z.string(),
  applyTagChanges: z.boolean().default(false),
});
export type MovePhotosBody = z.infer<typeof MovePhotosBody>;
export const AlbumPhotosBody = z.object({ ids: z.array(z.string()).min(1).max(500) });
export type AlbumPhotosBody = z.infer<typeof AlbumPhotosBody>;
/** Add to album; `applyTagChanges` ("Also add this album's tags") adds its default tags to each photo. */
export const AddToAlbumBody = AlbumPhotosBody.extend({ applyTagChanges: z.boolean().default(false) });
export type AddToAlbumBody = z.infer<typeof AddToAlbumBody>;

/* ---------- studio: trash ---------- */
export const TrashedPhoto = PhotoCard.extend({ trashedAt: z.string(), trashedByName: z.string().nullable() });
export type TrashedPhoto = z.infer<typeof TrashedPhoto>;
/** GET /api/studio/trash/:id: the Trash viewer (read-only details, plus when and by whom it was trashed). */
export const TrashedPhotoDetail = StudioPhotoDetail.extend({ trashedAt: z.string(), trashedByName: z.string().nullable() });
export type TrashedPhotoDetail = z.infer<typeof TrashedPhotoDetail>;
/** Photos in Trash (newest first), except ones waiting in a removal request. Deleted permanently after 30 days. */
export const TrashResponse = z.object({ items: z.array(TrashedPhoto) });
export type TrashResponse = z.infer<typeof TrashResponse>;
export const TrashPhotosBody = z.object({ ids: z.array(z.string()).min(1).max(500) });
export type TrashPhotosBody = z.infer<typeof TrashPhotosBody>;
export const TRASH_RETENTION_DAYS = 30;
/** "Add to Glean": Flock → the official collection (internal key `glean`), into an album. */
export const AddToGleanBody = z.object({ ids: z.array(z.string()).min(1).max(500), albumId: z.string() });
export type AddToGleanBody = z.infer<typeof AddToGleanBody>;
export const PhotoChangeResponse = z.object({ changed: z.number(), activityId: z.string().nullable() });
export type PhotoChangeResponse = z.infer<typeof PhotoChangeResponse>;

/** POST /api/studio/release-preview: selected photos, or every unreleased photo of an album. */
export const ReleasePreviewBody = z
  .object({ ids: z.array(z.string()).min(1).max(500).optional(), albumId: z.string().optional() })
  .refine((b) => !!b.ids !== !!b.albumId, 'Pass either ids or albumId');
export type ReleasePreviewBody = z.infer<typeof ReleasePreviewBody>;
export const ReleasePreviewResponse = z.object({
  /** Unreleased photos that would be released. */
  count: z.number(),
  /** How many of them are also in other albums. */
  alsoInOtherAlbums: z.number(),
});
export type ReleasePreviewResponse = z.infer<typeof ReleasePreviewResponse>;

/** Left-nav counts in Studio. */
export const StudioCounts = z.object({
  albums: z.number(),
  unreleased: z.number(),
  restricted: z.number().optional(),
  removalRequests: z.number().optional(),
  trash: z.number().optional(),
  /** Possible duplicates: groups (media). */
  duplicates: z.number().optional(),
});
export type StudioCounts = z.infer<typeof StudioCounts>;
/** GET /api/studio/badges: the area switcher's Studio badge, polled every minute — open removal requests the caller can review (0 unless media). */
export const StudioBadges = z.object({ removalRequests: z.number() });
export type StudioBadges = z.infer<typeof StudioBadges>;

/* ---------- studio: possible duplicates (media) ---------- */
/** One photo in a duplicates group, with what tells them apart. `copiedFrom` marks "Add as a separate copy" copies. */
export const DuplicatePhoto = PhotoCard.extend({
  camera: z.string().optional(),
  albumName: z.string().nullable(),
  copiedFrom: z.string().optional(),
});
export type DuplicatePhoto = z.infer<typeof DuplicatePhoto>;
/** `burst`: same camera, taken within 2 seconds; `similar`: perceptual hashes at most 6 bits apart. */
export const DuplicateGroup = z.object({ reasons: z.array(z.enum(['burst', 'similar'])), photos: z.array(DuplicatePhoto) });
export type DuplicateGroup = z.infer<typeof DuplicateGroup>;
/** GET /api/studio/duplicates: groups, newest first. */
export const DuplicatesResponse = z.object({ groups: z.array(DuplicateGroup) });
export type DuplicatesResponse = z.infer<typeof DuplicatesResponse>;
/** POST /api/studio/duplicates/dismiss: "Not duplicates" for a group (every pair among `ids` is remembered). */
export const DismissDuplicatesBody = z.object({ ids: z.array(z.string()).min(2).max(50) });
export type DismissDuplicatesBody = z.infer<typeof DismissDuplicatesBody>;

/* ---------- studio: activity ---------- */
/** An activity entry as listed (the undo snapshot stays on the server). */
export const ActivityView = Activity.omit({ undo: true }).extend({
  count: z.number(),
  undoable: z.boolean(),
});
export type ActivityView = z.infer<typeof ActivityView>;
export const ActivityResponse = z.object({ items: z.array(ActivityView), cursor: z.string().nullable() });
export type ActivityResponse = z.infer<typeof ActivityResponse>;
/**
 * GET /api/studio/activity: filters combine (AND), newest first. `q` matches the summary, the person, the album name and
 * the file names of the entry's photos; `actions` is comma-separated in the URL; `from` / `to` are ISO times (inclusive).
 * Undone entries are always included (they show struck through).
 */
export const StudioActivityQuery = z.object({
  q: z.string().trim().min(1).max(100).optional(),
  actorId: z.string().optional(),
  actions: z.array(ActivityAction).optional(),
  albumId: z.string().optional(),
  from: z.string().datetime().optional(),
  to: z.string().datetime().optional(),
  cursor: z.string().optional(),
});
export type StudioActivityQuery = z.infer<typeof StudioActivityQuery>;
/** GET /api/studio/activity/actors: people who can appear in the log (for the Person filter). */
export const ActivityActorsResponse = z.object({ items: z.array(z.object({ id: z.string(), name: z.string() })) });
export type ActivityActorsResponse = z.infer<typeof ActivityActorsResponse>;
export const UndoResponse = z.object({ undone: z.number(), skipped: z.number(), total: z.number(), activityId: z.string() });
export type UndoResponse = z.infer<typeof UndoResponse>;

/* ---------- studio: invites ---------- */
export const CreateInviteBody = z.object({
  expiresInDays: z.union([z.literal(7), z.literal(30), z.literal(90)]).default(30),
});
export type CreateInviteBody = z.infer<typeof CreateInviteBody>;
export const InviteView = z.object({
  id: z.string(),
  albumId: z.string(),
  url: z.string(),
  createdByName: z.string(),
  createdAt: z.string(),
  expiresAt: z.string(),
});
export type InviteView = z.infer<typeof InviteView>;
export const InvitesResponse = z.object({ items: z.array(InviteView) });
export type InvitesResponse = z.infer<typeof InvitesResponse>;
/** GET /api/join/:token */
export const JoinPreview = z.object({
  albumId: z.string(),
  albumName: z.string(),
  invitedBy: z.string(),
  expiresAt: z.string(),
  alreadyMember: z.boolean(),
});
export type JoinPreview = z.infer<typeof JoinPreview>;
export const JoinResponse = z.object({ albumId: z.string(), albumName: z.string() });
export type JoinResponse = z.infer<typeof JoinResponse>;

/* ---------- admin: activity ---------- */
export const AdminActivityQuery = z.object({
  actorId: z.string().optional(),
  action: Activity.shape.action.optional(),
  albumId: z.string().optional(),
  cursor: z.string().optional(),
});
export type AdminActivityQuery = z.infer<typeof AdminActivityQuery>;

/* ---------- admin: events (an admin-managed list; the tag name stays fixed once created) ---------- */
export const EventLabelBody = z.object({ label: z.string().trim().min(1).max(64) });
export type EventLabelBody = z.infer<typeof EventLabelBody>;
/** GET /api/admin/events/:name/usage: photos (any status) and albums (default tags) using an event. */
export const EventUsage = z.object({ photos: z.number(), albums: z.number() });
export type EventUsage = z.infer<typeof EventUsage>;
/**
 * DELETE /api/admin/events/:name always removes the event: `demoted` (used: it becomes a free tag, photos and albums keep
 * it) or `deleted` (unused).
 */
export const RemoveEventResponse = EventUsage.extend({ result: z.enum(['demoted', 'deleted']) });
export type RemoveEventResponse = z.infer<typeof RemoveEventResponse>;

/* ---------- album access (Studio; media and admins) ---------- */
/** One person's access to one album: through an invite link or added directly. */
export const AccessGrant = z.object({
  albumId: z.string(),
  albumName: z.string(),
  userId: z.string(),
  userName: z.string(),
  userEmail: z.string(),
  how: z.enum(['invite', 'direct']),
  grantedByName: z.string().nullable(),
  grantedAt: z.string(),
});
export type AccessGrant = z.infer<typeof AccessGrant>;
/** GET /api/studio/access?albumId=&userId=: grants, newest first. */
export const AccessGrantsQuery = z.object({ albumId: z.string().optional(), userId: z.string().optional() });
export type AccessGrantsQuery = z.infer<typeof AccessGrantsQuery>;
export const AccessGrantsResponse = z.object({ items: z.array(AccessGrant) });
export type AccessGrantsResponse = z.infer<typeof AccessGrantsResponse>;
/** POST /api/studio/access: give an active user of type user upload + tag access to one album (DELETE /access/:albumId/:userId removes it). */
export const GrantAccessBody = z.object({ userId: z.string().min(1), albumId: z.string().min(1) });
export type GrantAccessBody = z.infer<typeof GrantAccessBody>;
/** GET /api/studio/access/users?q=&albumId=: active users of type user matching name, email or church (minus those who already have the album). */
export const AccessCandidatesResponse = z.object({ items: z.array(z.object({ id: z.string(), name: z.string(), email: z.string(), church: z.string() })) });
export type AccessCandidatesResponse = z.infer<typeof AccessCandidatesResponse>;
/** GET /api/studio/access/albums: every album (id + name, by name), for the Add access picker. */
export const AccessAlbumsResponse = z.object({ items: z.array(z.object({ id: z.string(), name: z.string() })) });
export type AccessAlbumsResponse = z.infer<typeof AccessAlbumsResponse>;
